/**
 * InsightCard — generic card wrapper for dashboard sections.
 * All dashboard cards share this visual unit: title + optional subtitle,
 * optional action slot (e.g. "View all →"), and a content area.
 *
 * Retro redesign: card chrome comes from the MUI theme (dark surface +
 * subtle border). Title is JetBrains Mono uppercase to feel like a panel
 * label on a console.
 */

import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

export interface InsightCardProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}

export function InsightCard({
  title,
  subtitle,
  action,
  children,
}: InsightCardProps): ReactElement {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              color: 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.8125rem',
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
      </Box>

      <Divider />

      <CardContent sx={{ flexGrow: 1, px: 2.5, py: 2 }}>{children}</CardContent>
    </Card>
  );
}
